<script setup lang="ts">
import Mdi from '@/corePagesModule/components/Mdi.vue';
import { IRecommendEditPanelResizeState } from '@/recommendOrderModule/pages/interfaces/recommendOrder';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

const props = defineProps<{
  isActive: boolean;
  title: string;
  expandedLessDesc?: string;
  expandedMoreDesc?: string;
  isGrowing?: boolean;
  resizable?: boolean;
  resizeState?: IRecommendEditPanelResizeState;
}>();

const emits = defineEmits<{
  (e: 'update:resizeState', value: IRecommendEditPanelResizeState): void;
}>();

const isExpanded = ref<boolean>(true);
const wrapperRef = ref<HTMLElement | null>(null);
const isResizing = ref<boolean>(false);
const resizedHeight = ref<number>();
const hasUserResized = ref(false);

let resizingPointerId: number | undefined;
let resizeHandle: HTMLElement | undefined;
let startY = 0;
let startHeight = 0;
let initialHeight: number | undefined;

const textIconState = computed((): [label: string | undefined, icon: string] =>
  isExpanded.value ? [props.expandedLessDesc, 'expand_less'] : [props.expandedMoreDesc, 'expand_more']
);

/**
 * リサイズ状態を親へ保存する
 */
const saveResizeState = () => {
  emits('update:resizeState', {
    height: resizedHeight.value,
    minHeight: initialHeight,
    hasUserResized: hasUserResized.value,
  });
};

const wrapperStyle = computed(() => {
  if ((!isResizing.value && !hasUserResized.value) || resizedHeight.value == null || !isExpanded.value) {
    return;
  }

  return {
    height: `${resizedHeight.value}px`,
    flex: '0 0 auto',
  };
});

/**
 * リサイズ開始
 * @param event - イベント
 */
const startResize = (event: PointerEvent) => {
  if (!wrapperRef.value || event.button !== 0) return;

  event.preventDefault();

  const currentHeight = wrapperRef.value.getBoundingClientRect().height;

  // flex-growで表示されている場合は、現在の割当高をリサイズ下限へ反映する。
  // 保存済みの高さを先に適用するとpointerdown時にパネルが縮むため、固定高も現在値へ同期する。
  if (!hasUserResized.value) {
    initialHeight = currentHeight;
    resizedHeight.value = currentHeight;
  }

  resizeHandle = event.currentTarget as HTMLElement;
  resizingPointerId = event.pointerId;
  resizeHandle.setPointerCapture(event.pointerId);

  isResizing.value = true;
  document.documentElement.classList.add('is-resizing-vertical');

  // 初回の高さを最小サイズとして保存
  initialHeight ??= currentHeight;

  startY = event.clientY;
  startHeight = currentHeight;
};

/**
 * リサイズ状態を解除する
 */
const finishResize = () => {
  const handle = resizeHandle;
  const pointerId = resizingPointerId;

  isResizing.value = false;
  resizingPointerId = undefined;
  resizeHandle = undefined;
  document.documentElement.classList.remove('is-resizing-vertical');

  if (handle && pointerId !== undefined && handle.hasPointerCapture(pointerId)) {
    handle.releasePointerCapture(pointerId);
  }

  saveResizeState();
};

/**
 * リサイズイベント
 * @param event - イベント
 */
const resize = (event: PointerEvent) => {
  if (!isResizing.value || event.pointerId !== resizingPointerId) return;

  // ウィンドウ外でpointerupを取り逃した場合、再入場時にリサイズを終了する
  if (event.buttons === 0) {
    finishResize();
    return;
  }

  event.preventDefault();

  const calculatedHeight = startHeight + event.clientY - startY;
  const nextHeight = Math.max(initialHeight ?? 0, calculatedHeight);

  resizedHeight.value = nextHeight;
  hasUserResized.value = initialHeight != null && nextHeight > initialHeight;
};

/**
 * リサイズ停止
 * @param event - イベント
 */
const stopResize = (event?: PointerEvent) => {
  if (event && resizingPointerId !== undefined && event.pointerId !== resizingPointerId) {
    return;
  }

  finishResize();
};

/**
 * 開閉トグル
 */
const toggleExpanded = () => {
  if (props.isActive) return;
  isExpanded.value = !isExpanded.value;
};

/**
 * 初期高さリセット
 */
const resetInitialHeight = () => {
  if (hasUserResized.value) return;
  initialHeight = undefined;
  resizedHeight.value = undefined;
};

watch(
  () => props.isActive,
  (isActive) => {
    if (isActive) {
      isExpanded.value = true;
    }
  }
);

watch(
  () => props.resizeState,
  (resizeState) => {
    if (isResizing.value) return;
    resizedHeight.value = resizeState?.height;
    initialHeight = resizeState?.minHeight;
    hasUserResized.value = resizeState?.hasUserResized ?? false;
  },
  { immediate: true }
);

onMounted(() => {
  window.addEventListener('blur', finishResize);
});

onBeforeUnmount(() => {
  window.removeEventListener('blur', finishResize);
  finishResize();
  resetInitialHeight();
});
</script>
<template>
  <div
    ref="wrapperRef"
    class="recommend-edit__wrapper"
    :class="{
      'recommend-edit__wrapper--closed': !isExpanded,
      'recommend-edit__wrapper--active': isActive,
      'recommend-edit__wrapper--grow': isGrowing && isExpanded,
    }"
    :style="wrapperStyle"
  >
    <div class="recommend-edit__header">
      <span class="recommend-edit__title">
        {{ props.title }}
      </span>
      <button
        v-if="!props.isActive"
        v-tooltip="textIconState[0]"
        :aria-label="textIconState[0]"
        class="recommend-edit__expand"
        @click="toggleExpanded"
      >
        <Mdi :icon="textIconState[1]" />
      </button>
    </div>
    <div v-show="isExpanded" class="recommend-edit__content" :class="{ 'recommend-edit__content--inactive': !isActive }">
      <slot />
    </div>
    <div
      v-if="props.resizable && isExpanded"
      class="recommend-edit__resize-handle"
      @pointerdown="startResize"
      @pointermove="resize"
      @pointerup="stopResize"
      @pointercancel="stopResize"
      @lostpointercapture="finishResize"
    />
  </div>
</template>
<style lang="scss" scoped>
.recommend-edit {
  &__wrapper {
    position: relative;
    display: grid;
    // リサイズ時もスロット内の操作領域が収まる高さを下限にする
    grid-template-rows: max-content minmax(min-content, 1fr);
    gap: 8px;
    padding: 10px 14px;
    background-color: $semanticWhite !important;
    border-radius: 8px;
    border: 1px solid $semanticPrimary;
    box-shadow: 0px 0px 4px 0px rgba(30, 146, 239, 0.5);
    flex: 0 0 auto;

    &--active {
      border: 2px solid $semanticPrimary;
      box-shadow: 0px 0px 16px 0px rgba(30, 146, 239, 0.5);
    }

    &--closed {
      flex-grow: 0;
      padding-bottom: 6px;
    }

    &--grow {
      flex: 1 1 0;
      min-height: min-content;
    }
  }

  &__header {
    padding: 0px 2.5px 0px 2.5px;
    display: grid;
    grid-template-columns: auto 1fr;
    column-gap: 8px;
    height: max-content;
  }

  &__title {
    font-family: Inter;
    font-size: $fontSizeM;
    color: $textDefault;
    line-height: 23px;
    font-weight: 700;
    justify-self: start;
  }

  &__expand {
    width: 24px;
    height: 24px;
    justify-self: end;
  }

  &__content {
    min-height: min-content;
    &--inactive {
      opacity: $transparency50;
      pointer-events: none;
    }
  }

  &__resize-handle {
    position: absolute;
    left: 50%;
    bottom: -5px;
    transform: translateX(-50%);

    display: flex;
    align-items: center;
    justify-content: center;

    width: 128px;
    height: 10px;

    border-radius: 999px;
    background: #1e92ef;

    cursor: ns-resize;

    &::after {
      content: '';
      width: 36px;
      height: 4px;
      border-top: 1px solid white;
      border-bottom: 1px solid white;
      opacity: 0.9;
    }
  }
}

:global(html.is-resizing-vertical),
:global(html.is-resizing-vertical *) {
  cursor: ns-resize !important;
  user-select: none !important;
}
</style>
